/* eslint-disable max-classes-per-file */
/* eslint-disable no-misleading-character-class */
/**
 * Main funcitons to show/choose cards
 * Dependencies: core.js // asyncRequest()
 *
 * Usage:
 *   const cardEditor = new CardEditor({
 *     initialValues: {
 *      cardId: 'Z2lkOi8vc2hvcGlmeS9Qcm9kdWN0VmFyaWFudC80MTU5OTY5MTA5NjI1OA==',
 *      cardMessage: 'Message'
 *     },
 *     title: 'Add a free <b>gift card</b>',
 *     saveBtnLabel: 'Checkout',
 *     onSelect: (data) => console.log(data),
 * 		 onError: (error, editor) => {console.log(error); editor.destroy()}
 *     readyCallback: (editor) => {
 *       showCardEditor.addEventListener('click', () => {
 *         editor.openSelector();
 *       });
 *     },
 *   });
 * 	await cardEditor.createEditor();
 */

const Clipboard = Quill.import("modules/clipboard");
const Delta = Quill.import("delta");
class PlainClipboard extends Clipboard {
	onPaste(e) {
		e.preventDefault();
		const range = this.quill.getSelection();
		const text = e.clipboardData.getData("text/plain");
		const delta = new Delta().retain(range.index).delete(range.length).insert(text);
		const index = text.length + range.index;
		const length = 0;
		this.quill.updateContents(delta, "silent");
		this.quill.setSelection(index, length, "silent");
		this.quill.scrollIntoView();
	}
}
// eslint-disable-next-line no-unused-vars
class CardEditor {
	constructor({ initialValues, cardsUrl, title, saveBtnLabel, onSelect, onClose, onError, readyCallback }) {
		/**
		 * Options
		 */
		this.maxlengthMsg = [120, 720, 120]; // [cardHeader, cardBody, cardFooter]
		this.allowEmojis = false;
		this.defaultTitle = title || "SELECT YOUR DESIGN";
		this.saveLabel = saveBtnLabel || "Save changes";
		this.cardsUrl = cardsUrl || "/cards/greeting-cards/index.html";
		this.firstOpen = "CARDS"; // CARDS | MESSAGE
		this.errorMessagesActives = [];
		this.errorMessages = {
			cardNotSelected:
				'<i class="fal fa-exclamation-circle"></i>Sorry, You have not selected a card, please select or choose no card option',
			blankCard: `<i class="fal fa-exclamation-circle"></i>Did you mean to leave the message blank?<br/>
              Please check your message or select the 'Leave Card Blank Inside' option`,
			noEmojis:
				'<i class="fal fa-exclamation-circle"></i>Sorry, we don\'t currently support Emojis, so they will be removed',
		};

		this.emojiRegex =
			/\uD83C\uDFF4\uDB40\uDC67\uDB40\uDC62?:\uDB40\uDC77\uDB40\uDC6C\uDB40\uDC73|\uDB40\uDC73\uDB40\uDC63\uDB40\uDC74|\uDB40\uDC65\uDB40\uDC6E\uDB40\uDC67\uDB40\uDC7F|\uD83D\uDC69\u200D\uD83D\uDC69\u200D(?:\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D[\uD83D\uDC66\uD83D\uDC67])|\uD83D\uDC68(?:\uD83C\uDFFF\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68[\uD83C\uDFFB-\uD83C\uDFFE]|[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD])|\uD83C\uDFFE\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68[\uD83C\uDFFB-\uD83C\uDFFD\uD83C\uDFFF]|[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD])|\uD83C\uDFFD\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68[\uD83C\uDFFB\uD83C\uDFFC\uD83C\uDFFE\uD83C\uDFFF]|[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD])|\uD83C\uDFFC\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68[\uD83C\uDFFB\uD83C\uDFFD-\uD83C\uDFFF]|[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD])|\uD83C\uDFFB\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68[\uD83C\uDFFC-\uD83C\uDFFF]|[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD])|\u200D(?:\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D)?\uD83D\uDC68|[\uD83D\uDC68\uD83D\uDC69]\u200D(?:\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D[\uD83D\uDC66\uD83D\uDC67])|\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D[\uD83D\uDC66\uD83D\uDC67]|[\uD83D\uDC68\uD83D\uDC69]\u200D[\uD83D\uDC66\uD83D\uDC67]|[\u2695\u2696\u2708]\uFE0F|[\uD83D\uDC66\uD83D\uDC67]|[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD])|(?:\uD83C\uDFFF\u200D[\u2695\u2696\u2708]|\uD83C\uDFFE\u200D[\u2695\u2696\u2708]|\uD83C\uDFFD\u200D[\u2695\u2696\u2708]|\uD83C\uDFFC\u200D[\u2695\u2696\u2708]|\uD83C\uDFFB\u200D[\u2695\u2696\u2708])\uFE0F|[\uD83C\uDFFB-\uD83C\uDFFF])|\uD83E\uDDD1(?:[\uD83C\uDFFB-\uD83C\uDFFF]\u200D(?:\uD83E\uDD1D\u200D\uD83E\uDDD1[\uD83C\uDFFB-\uD83C\uDFFF]|[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF84\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD])|\u200D(?:\uD83E\uDD1D\u200D\uD83E\uDDD1|[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF84\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD]))|\uD83D\uDC69(?:\u200D(?:\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D[\uD83D\uDC68\uD83D\uDC69]|[\uD83D\uDC68\uD83D\uDC69])|[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD])|\uD83C\uDFFF\u200D[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD]|\uD83C\uDFFE\u200D[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD]|\uD83C\uDFFD\u200D[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD]|\uD83C\uDFFC\u200D[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD]|\uD83C\uDFFB\u200D[\uD83C\uDF3E\uD83C\uDF73\uD83C\uDF7C\uD83C\uDF93\uD83C\uDFA4\uD83C\uDFA8\uD83C\uDFEB\uD83C\uDFED\uD83D\uDCBB\uD83D\uDCBC\uD83D\uDD27\uD83D\uDD2C\uD83D\uDE80\uD83D\uDE92\uD83E\uDDAF-\uD83E\uDDB3\uD83E\uDDBC\uD83E\uDDBD])|\uD83D\uDC69\uD83C\uDFFF\u200D\uD83E\uDD1D\u200D[\uD83D\uDC68\uD83D\uDC69][\uD83C\uDFFB-\uD83C\uDFFE]|\uD83D\uDC69\uD83C\uDFFE\u200D\uD83E\uDD1D\u200D[\uD83D\uDC68\uD83D\uDC69][\uD83C\uDFFB-\uD83C\uDFFD\uD83C\uDFFF]|\uD83D\uDC69\uD83C\uDFFD\u200D\uD83E\uDD1D\u200D[\uD83D\uDC68\uD83D\uDC69][\uD83C\uDFFB\uD83C\uDFFC\uD83C\uDFFE\uD83C\uDFFF]|\uD83D\uDC69\uD83C\uDFFC\u200D\uD83E\uDD1D\u200D[\uD83D\uDC68\uD83D\uDC69][\uD83C\uDFFB\uD83C\uDFFD-\uD83C\uDFFF]|\uD83D\uDC69\uD83C\uDFFB\u200D\uD83E\uDD1D\u200D[\uD83D\uDC68\uD83D\uDC69][\uD83C\uDFFC-\uD83C\uDFFF]|\uD83D\uDC69\u200D\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC69\u200D\uD83D\uDC69\u200D[\uD83D\uDC66\uD83D\uDC67]|(?:\uD83D\uDC41\uFE0F\u200D\uD83D\uDDE8|\uD83D\uDC69(?:\uD83C\uDFFF\u200D[\u2695\u2696\u2708]|\uD83C\uDFFE\u200D[\u2695\u2696\u2708]|\uD83C\uDFFD\u200D[\u2695\u2696\u2708]|\uD83C\uDFFC\u200D[\u2695\u2696\u2708]|\uD83C\uDFFB\u200D[\u2695\u2696\u2708]|\u200D[\u2695\u2696\u2708])|\uD83C\uDFF3\uFE0F\u200D\u26A7|\uD83E\uDDD1(?:[\uD83C\uDFFB-\uD83C\uDFFF]\u200D[\u2695\u2696\u2708]|\u200D[\u2695\u2696\u2708])|\uD83D\uDC3B\u200D\u2744|[\uD83C\uDFC3\uD83C\uDFC4\uD83C\uDFCA\uD83D\uDC6E\uD83D\uDC70\uD83D\uDC71\uD83D\uDC73\uD83D\uDC77\uD83D\uDC81\uD83D\uDC82\uD83D\uDC86\uD83D\uDC87\uD83D\uDE45-\uD83D\uDE47\uD83D\uDE4B\uD83D\uDE4D\uD83D\uDE4E\uD83D\uDEA3\uD83D\uDEB4-\uD83D\uDEB6\uD83E\uDD26\uD83E\uDD35\uD83E\uDD37-\uD83E\uDD39\uD83E\uDD3D\uD83E\uDD3E\uD83E\uDDB8\uD83E\uDDB9\uD83E\uDDCD-\uD83E\uDDCF\uD83E\uDDD6-\uD83E\uDDDD][\uD83C\uDFFB-\uD83C\uDFFF]\u200D[\u2640\u2642]|[\u26F9\uD83C\uDFCB\uD83C\uDFCC\uD83D\uDD75](?:\uFE0F\u200D[\u2640\u2642]|[\uD83C\uDFFB-\uD83C\uDFFF]\u200D[\u2640\u2642])|\uD83C\uDFF4\u200D\u2620|[\uD83C\uDFC3\uD83C\uDFC4\uD83C\uDFCA\uD83D\uDC6E-\uD83D\uDC71\uD83D\uDC73\uD83D\uDC77\uD83D\uDC81\uD83D\uDC82\uD83D\uDC86\uD83D\uDC87\uD83D\uDE45-\uD83D\uDE47\uD83D\uDE4B\uD83D\uDE4D\uD83D\uDE4E\uD83D\uDEA3\uD83D\uDEB4-\uD83D\uDEB6\uD83E\uDD26\uD83E\uDD35\uD83E\uDD37-\uD83E\uDD39\uD83E\uDD3C-\uD83E\uDD3E\uD83E\uDDB8\uD83E\uDDB9\uD83E\uDDCD-\uD83E\uDDCF\uD83E\uDDD6-\uD83E\uDDDF]\u200D[\u2640\u2642])\uFE0F|\uD83D\uDC69\u200D\uD83D\uDC67\u200D[\uD83D\uDC66\uD83D\uDC67]|\uD83C\uDFF3\uFE0F\u200D\uD83C\uDF08|\uD83D\uDC69\u200D\uD83D\uDC67|\uD83D\uDC69\u200D\uD83D\uDC66|\uD83D\uDC15\u200D\uD83E\uDDBA|\uD83C\uDDFD\uD83C\uDDF0|\uD83C\uDDF6\uD83C\uDDE6|\uD83C\uDDF4\uD83C\uDDF2|\uD83D\uDC08\u200D\u2B1B|\uD83E\uDDD1[\uD83C\uDFFB-\uD83C\uDFFF]|\uD83D\uDC69[\uD83C\uDFFB-\uD83C\uDFFF]|\uD83C\uDDFF[\uD83C\uDDE6\uD83C\uDDF2\uD83C\uDDFC]|\uD83C\uDDFE[\uD83C\uDDEA\uD83C\uDDF9]|\uD83C\uDDFC[\uD83C\uDDEB\uD83C\uDDF8]|\uD83C\uDDFB[\uD83C\uDDE6\uD83C\uDDE8\uD83C\uDDEA\uD83C\uDDEC\uD83C\uDDEE\uD83C\uDDF3\uD83C\uDDFA]|\uD83C\uDDFA[\uD83C\uDDE6\uD83C\uDDEC\uD83C\uDDF2\uD83C\uDDF3\uD83C\uDDF8\uD83C\uDDFE\uD83C\uDDFF]|\uD83C\uDDF9[\uD83C\uDDE6\uD83C\uDDE8\uD83C\uDDE9\uD83C\uDDEB-\uD83C\uDDED\uD83C\uDDEF-\uD83C\uDDF4\uD83C\uDDF7\uD83C\uDDF9\uD83C\uDDFB\uD83C\uDDFC\uD83C\uDDFF]|\uD83C\uDDF8[\uD83C\uDDE6-\uD83C\uDDEA\uD83C\uDDEC-\uD83C\uDDF4\uD83C\uDDF7-\uD83C\uDDF9\uD83C\uDDFB\uD83C\uDDFD-\uD83C\uDDFF]|\uD83C\uDDF7[\uD83C\uDDEA\uD83C\uDDF4\uD83C\uDDF8\uD83C\uDDFA\uD83C\uDDFC]|\uD83C\uDDF5[\uD83C\uDDE6\uD83C\uDDEA-\uD83C\uDDED\uD83C\uDDF0-\uD83C\uDDF3\uD83C\uDDF7-\uD83C\uDDF9\uD83C\uDDFC\uD83C\uDDFE]|\uD83C\uDDF3[\uD83C\uDDE6\uD83C\uDDE8\uD83C\uDDEA-\uD83C\uDDEC\uD83C\uDDEE\uD83C\uDDF1\uD83C\uDDF4\uD83C\uDDF5\uD83C\uDDF7\uD83C\uDDFA\uD83C\uDDFF]|\uD83C\uDDF2[\uD83C\uDDE6\uD83C\uDDE8-\uD83C\uDDED\uD83C\uDDF0-\uD83C\uDDFF]|\uD83C\uDDF1[\uD83C\uDDE6-\uD83C\uDDE8\uD83C\uDDEE\uD83C\uDDF0\uD83C\uDDF7-\uD83C\uDDFB\uD83C\uDDFE]|\uD83C\uDDF0[\uD83C\uDDEA\uD83C\uDDEC-\uD83C\uDDEE\uD83C\uDDF2\uD83C\uDDF3\uD83C\uDDF5\uD83C\uDDF7\uD83C\uDDFC\uD83C\uDDFE\uD83C\uDDFF]|\uD83C\uDDEF[\uD83C\uDDEA\uD83C\uDDF2\uD83C\uDDF4\uD83C\uDDF5]|\uD83C\uDDEE[\uD83C\uDDE8-\uD83C\uDDEA\uD83C\uDDF1-\uD83C\uDDF4\uD83C\uDDF6-\uD83C\uDDF9]|\uD83C\uDDED[\uD83C\uDDF0\uD83C\uDDF2\uD83C\uDDF3\uD83C\uDDF7\uD83C\uDDF9\uD83C\uDDFA]|\uD83C\uDDEC[\uD83C\uDDE6\uD83C\uDDE7\uD83C\uDDE9-\uD83C\uDDEE\uD83C\uDDF1-\uD83C\uDDF3\uD83C\uDDF5-\uD83C\uDDFA\uD83C\uDDFC\uD83C\uDDFE]|\uD83C\uDDEB[\uD83C\uDDEE-\uD83C\uDDF0\uD83C\uDDF2\uD83C\uDDF4\uD83C\uDDF7]|\uD83C\uDDEA[\uD83C\uDDE6\uD83C\uDDE8\uD83C\uDDEA\uD83C\uDDEC\uD83C\uDDED\uD83C\uDDF7-\uD83C\uDDFA]|\uD83C\uDDE9[\uD83C\uDDEA\uD83C\uDDEC\uD83C\uDDEF\uD83C\uDDF0\uD83C\uDDF2\uD83C\uDDF4\uD83C\uDDFF]|\uD83C\uDDE8[\uD83C\uDDE6\uD83C\uDDE8\uD83C\uDDE9\uD83C\uDDEB-\uD83C\uDDEE\uD83C\uDDF0-\uD83C\uDDF5\uD83C\uDDF7\uD83C\uDDFA-\uD83C\uDDFF]|\uD83C\uDDE7[\uD83C\uDDE6\uD83C\uDDE7\uD83C\uDDE9-\uD83C\uDDEF\uD83C\uDDF1-\uD83C\uDDF4\uD83C\uDDF6-\uD83C\uDDF9\uD83C\uDDFB\uD83C\uDDFC\uD83C\uDDFE\uD83C\uDDFF]|\uD83C\uDDE6[\uD83C\uDDE8-\uD83C\uDDEC\uD83C\uDDEE\uD83C\uDDF1\uD83C\uDDF2\uD83C\uDDF4\uD83C\uDDF6-\uD83C\uDDFA\uD83C\uDDFC\uD83C\uDDFD\uD83C\uDDFF]|[#*0-9]\uFE0F\u20E3|[\uD83C\uDFC3\uD83C\uDFC4\uD83C\uDFCA\uD83D\uDC6E\uD83D\uDC70\uD83D\uDC71\uD83D\uDC73\uD83D\uDC77\uD83D\uDC81\uD83D\uDC82\uD83D\uDC86\uD83D\uDC87\uD83D\uDE45-\uD83D\uDE47\uD83D\uDE4B\uD83D\uDE4D\uD83D\uDE4E\uD83D\uDEA3\uD83D\uDEB4-\uD83D\uDEB6\uD83E\uDD26\uD83E\uDD35\uD83E\uDD37-\uD83E\uDD39\uD83E\uDD3D\uD83E\uDD3E\uD83E\uDDB8\uD83E\uDDB9\uD83E\uDDCD-\uD83E\uDDCF\uD83E\uDDD6-\uD83E\uDDDD][\uD83C\uDFFB-\uD83C\uDFFF]|[\u26F9\uD83C\uDFCB\uD83C\uDFCC\uD83D\uDD75][\uD83C\uDFFB-\uD83C\uDFFF]|[\u261D\u270A-\u270D\uD83C\uDF85\uD83C\uDFC2\uD83C\uDFC7\uD83D\uDC42\uD83D\uDC43\uD83D\uDC46-\uD83D\uDC50\uD83D\uDC66\uD83D\uDC67\uD83D\uDC6B-\uD83D\uDC6D\uD83D\uDC72\uD83D\uDC74-\uD83D\uDC76\uD83D\uDC78\uD83D\uDC7C\uD83D\uDC83\uD83D\uDC85\uD83D\uDCAA\uD83D\uDD74\uD83D\uDD7A\uD83D\uDD90\uD83D\uDD95\uD83D\uDD96\uD83D\uDE4C\uD83D\uDE4F\uD83D\uDEC0\uD83D\uDECC\uD83E\uDD0C\uD83E\uDD0F\uD83E\uDD18-\uD83E\uDD1C\uD83E\uDD1E\uD83E\uDD1F\uD83E\uDD30-\uD83E\uDD34\uD83E\uDD36\uD83E\uDD77\uD83E\uDDB5\uD83E\uDDB6\uD83E\uDDBB\uD83E\uDDD2-\uD83E\uDDD5][\uD83C\uDFFB-\uD83C\uDFFF]|[\u231A\u231B\u23E9-\u23EC\u23F0\u23F3\u25FD\u25FE\u2614\u2615\u2648-\u2653\u267F\u2693\u26A1\u26AA\u26AB\u26BD\u26BE\u26C4\u26C5\u26CE\u26D4\u26EA\u26F2\u26F3\u26F5\u26FA\u26FD\u2705\u270A\u270B\u2728\u274C\u274E\u2753-\u2755\u2757\u2795-\u2797\u27B0\u27BF\u2B1B\u2B1C\u2B50\u2B55\uD83C\uDC04\uD83C\uDCCF\uD83C\uDD8E\uD83C\uDD91-\uD83C\uDD9A\uD83C\uDDE6-\uD83C\uDDFF\uD83C\uDE01\uD83C\uDE1A\uD83C\uDE2F\uD83C\uDE32-\uD83C\uDE36\uD83C\uDE38-\uD83C\uDE3A\uD83C\uDE50\uD83C\uDE51\uD83C\uDF00-\uD83C\uDF20\uD83C\uDF2D-\uD83C\uDF35\uD83C\uDF37-\uD83C\uDF7C\uD83C\uDF7E-\uD83C\uDF93\uD83C\uDFA0-\uD83C\uDFCA\uD83C\uDFCF-\uD83C\uDFD3\uD83C\uDFE0-\uD83C\uDFF0\uD83C\uDFF4\uD83C\uDFF8-\uD83D\uDC3E\uD83D\uDC40\uD83D\uDC42-\uD83D\uDCFC\uD83D\uDCFF-\uD83D\uDD3D\uD83D\uDD4B-\uD83D\uDD4E\uD83D\uDD50-\uD83D\uDD67\uD83D\uDD7A\uD83D\uDD95\uD83D\uDD96\uD83D\uDDA4\uD83D\uDDFB-\uD83D\uDE4F\uD83D\uDE80-\uD83D\uDEC5\uD83D\uDECC\uD83D\uDED0-\uD83D\uDED2\uD83D\uDED5-\uD83D\uDED7\uD83D\uDEEB\uD83D\uDEEC\uD83D\uDEF4-\uD83D\uDEFC\uD83D\uDFE0-\uD83D\uDFEB\uD83E\uDD0C-\uD83E\uDD3A\uD83E\uDD3C-\uD83E\uDD45\uD83E\uDD47-\uD83E\uDD78\uD83E\uDD7A-\uD83E\uDDCB\uD83E\uDDCD-\uD83E\uDDFF\uD83E\uDE70-\uD83E\uDE74\uD83E\uDE78-\uD83E\uDE7A\uD83E\uDE80-\uD83E\uDE86\uD83E\uDE90-\uD83E\uDEA8\uD83E\uDEB0-\uD83E\uDEB6\uD83E\uDEC0-\uD83E\uDEC2\uD83E\uDED0-\uD83E\uDED6]|[#*0-9\xA9\xAE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9\u21AA\u231A\u231B\u2328\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB-\u25FE\u2600-\u2604\u260E\u2611\u2614\u2615\u2618\u261D\u2620\u2622\u2623\u2626\u262A\u262E\u262F\u2638-\u263A\u2640\u2642\u2648-\u2653\u265F\u2660\u2663\u2665\u2666\u2668\u267B\u267E\u267F\u2692-\u2697\u2699\u269B\u269C\u26A0\u26A1\u26A7\u26AA\u26AB\u26B0\u26B1\u26BD\u26BE\u26C4\u26C5\u26C8\u26CE\u26CF\u26D1\u26D3\u26D4\u26E9\u26EA\u26F0-\u26F5\u26F7-\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2728\u2733\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763\u2764\u2795-\u2797\u27A1\u27B0\u27BF\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299\uD83C\uDC04\uD83C\uDCCF\uD83C\uDD70\uD83C\uDD71\uD83C\uDD7E\uD83C\uDD7F\uD83C\uDD8E\uD83C\uDD91-\uD83C\uDD9A\uD83C\uDDE6-\uD83C\uDDFF\uD83C\uDE01\uD83C\uDE02\uD83C\uDE1A\uD83C\uDE2F\uD83C\uDE32-\uD83C\uDE3A\uD83C\uDE50\uD83C\uDE51\uD83C\uDF00-\uD83C\uDF21\uD83C\uDF24-\uD83C\uDF93\uD83C\uDF96\uD83C\uDF97\uD83C\uDF99-\uD83C\uDF9B\uD83C\uDF9E-\uD83C\uDFF0\uD83C\uDFF3-\uD83C\uDFF5\uD83C\uDFF7-\uD83D\uDCFD\uD83D\uDCFF-\uD83D\uDD3D\uD83D\uDD49-\uD83D\uDD4E\uD83D\uDD50-\uD83D\uDD67\uD83D\uDD6F\uD83D\uDD70\uD83D\uDD73-\uD83D\uDD7A\uD83D\uDD87\uD83D\uDD8A-\uD83D\uDD8D\uD83D\uDD90\uD83D\uDD95\uD83D\uDD96\uD83D\uDDA4\uD83D\uDDA5\uD83D\uDDA8\uD83D\uDDB1\uD83D\uDDB2\uD83D\uDDBC\uD83D\uDDC2-\uD83D\uDDC4\uD83D\uDDD1-\uD83D\uDDD3\uD83D\uDDDC-\uD83D\uDDDE\uD83D\uDDE1\uD83D\uDDE3\uD83D\uDDE8\uD83D\uDDEF\uD83D\uDDF3\uD83D\uDDFA-\uD83D\uDE4F\uD83D\uDE80-\uD83D\uDEC5\uD83D\uDECB-\uD83D\uDED2\uD83D\uDED5-\uD83D\uDED7\uD83D\uDEE0-\uD83D\uDEE5\uD83D\uDEE9\uD83D\uDEEB\uD83D\uDEEC\uD83D\uDEF0\uD83D\uDEF3-\uD83D\uDEFC\uD83D\uDFE0-\uD83D\uDFEB\uD83E\uDD0C-\uD83E\uDD3A\uD83E\uDD3C-\uD83E\uDD45\uD83E\uDD47-\uD83E\uDD78\uD83E\uDD7A-\uD83E\uDDCB\uD83E\uDDCD-\uD83E\uDDFF\uD83E\uDE70-\uD83E\uDE74\uD83E\uDE78-\uD83E\uDE7A\uD83E\uDE80-\uD83E\uDE86\uD83E\uDE90-\uD83E\uDEA8\uD83E\uDEB0-\uD83E\uDEB6\uD83E\uDEC0-\uD83E\uDEC2\uD83E\uDED0-\uD83E\uDED6]\uFE0F|[\u261D\u26F9\u270A-\u270D\uD83C\uDF85\uD83C\uDFC2-\uD83C\uDFC4\uD83C\uDFC7\uD83C\uDFCA-\uD83C\uDFCC\uD83D\uDC42\uD83D\uDC43\uD83D\uDC46-\uD83D\uDC50\uD83D\uDC66-\uD83D\uDC78\uD83D\uDC7C\uD83D\uDC81-\uD83D\uDC83\uD83D\uDC85-\uD83D\uDC87\uD83D\uDC8F\uD83D\uDC91\uD83D\uDCAA\uD83D\uDD74\uD83D\uDD75\uD83D\uDD7A\uD83D\uDD90\uD83D\uDD95\uD83D\uDD96\uD83D\uDE45-\uD83D\uDE47\uD83D\uDE4B-\uD83D\uDE4F\uD83D\uDEA3\uD83D\uDEB4-\uD83D\uDEB6\uD83D\uDEC0\uD83D\uDECC\uD83E\uDD0C\uD83E\uDD0F\uD83E\uDD18-\uD83E\uDD1F\uD83E\uDD26\uD83E\uDD30-\uD83E\uDD39\uD83E\uDD3C-\uD83E\uDD3E\uD83E\uDD77\uD83E\uDDB5\uD83E\uDDB6\uD83E\uDDB8\uD83E\uDDB9\uD83E\uDDBB\uD83E\uDDCD-\uD83E\uDDCF\uD83E\uDDD1-\uD83E\uDDDD]/gu;

		/* Initialize Card values */
		this.categoryId = "";
		this.cardSKU = (initialValues && initialValues.cardSKU) || "";
		this.cardId = (initialValues && initialValues.cardId) || "";
		this.cardHeader = (initialValues && initialValues.cardHeader) || "";
		this.cardBody = (initialValues && initialValues.cardBody) || "";
		this.cardFooter = (initialValues && initialValues.cardFooter) || "";
		this.cardMessages = [this.cardHeader, this.cardBody, this.cardFooter];
		this.leaveBlank = (initialValues && initialValues.leaveBlank) || false;
		this.noCard = false;
		this.dataAttributes = null;
		this.readyCallback = typeof readyCallback === "function" ? readyCallback.bind(this) : () => null;
		this.onSelect = typeof onSelect === "function" ? onSelect.bind(this) : () => null;
		this.onClose = typeof onClose === "function" ? onClose.bind(this) : () => null;
		this.onError = typeof onError === "function" ? onError : () => null;
		this.cardHeaderEditor = null;
		this.cardBodyEditor = null;
		this.cardFooterEditor = null;
		this.cardMessageEditors = null;
	}

	cardIsBlank() {
		return this.cardMessageEditors.every((editor) => editor.getLength() === 1);
	}

	getTextContent() {
		return this.cardMessageEditors
			.filter((editor) => editor.getLength() > 1)
			.map((editor) => editor.getText())
			.join("\n");
	}

	finish() {
		this.checkMessage(true);
		if (this.cardId === "" && !this.noCard) {
			this.infoMessages(this.errorMessages.cardNotSelected);
			return;
		}
		if (this.cardIsBlank() && !this.leaveBlank && !this.noCard) {
			this.infoMessages(this.errorMessages.blankCard);
			return;
		}
		this.closeSelector(null, true);
		const data = {
			noCard: this.noCard,
			cardId: this.cardId,
			cardSKU: this.cardSKU,
			categoryId: this.categoryId,
			cardHeader: this.cardHeaderEditor.root.innerHTML,
			cardBody: this.cardBodyEditor.root.innerHTML,
			cardFooter: this.cardFooterEditor.root.innerHTML,
			cardTextContent: this.getTextContent(),
			leaveBlank: this.leaveBlank,
			previewUrl: this.previewUrl,
			dataAttributes: this.dataAttributes,
		};
		this.onSelect(data, this);
	}

	destroy() {
		this.cardsContainer.remove();
	}

	infoMessages(msg, remove = false) {
		if (!msg || msg === "") return;

		const index = this.errorMessagesActives.indexOf(msg);
		if (index > -1) {
			if (remove) {
				this.errorMessagesActives.splice(index, 1);
			}
		} else if (!remove) {
			this.errorMessagesActives.push(msg);
		}

		if (this.errorMessagesActives.length > 0) {
			this.messageInfo.innerHTML = this.errorMessagesActives.join("<br />");
			this.messageInfo.classList.remove("hide");
		} else {
			this.messageInfo.innerHTML = "";
			this.messageInfo.classList.add("hide");
		}
	}

	hasEmoji() {
		const emoji = this.cardMessageEditors.some((editor) => editor.getText().match(this.emojiRegex));
		if (emoji) {
			this.infoMessages(this.errorMessages.noEmojis);
			return;
		}
		this.infoMessages(this.errorMessages.noEmojis, true);
	}

	cleanEmojis() {
		this.cardMessageEditors.forEach((editor) => {
			// eslint-disable-next-line no-param-reassign
			editor.root.innerHTML = editor.root.innerHTML.replace(this.emojiRegex, "");
		});
	}

	disableEditors() {
		this.cardMessageEditors.forEach((editor, index) => {
			this.cardMessages[index] = editor.getContents();
			editor.setText("");
			editor.disable();
		});
	}

	enableEditors() {
		this.cardMessageEditors.forEach((editor, index) => {
			editor.setContents(this.cardMessages[index]);
			editor.enable();
		});
	}

	getMessageLengths() {
		return this.cardMessageEditors.map((editor, index) => {
			const messageCounter = this.messageCounters[index];
			const messageLength = editor.getLength() - 1;
			const maxMessageLength = this.maxlengthMsg[index];
			messageCounter.classList.toggle("limit-reached", messageLength >= maxMessageLength);
			return `${messageLength}/${maxMessageLength}`;
		});
	}

	handleBlankMessage(e) {
		if (typeof e === "object") this.leaveBlank = e.target.checked;

		if (this.leaveBlank) {
			this.messageCounters.forEach((counter) => counter.classList.add("hide"));
			this.disableEditors();
			this.infoMessages(this.errorMessages.blankCard, true);
		} else {
			this.messageCounters.forEach((counter) => counter.classList.remove("hide"));
			if (e) this.enableEditors();
			this.messageCounters.forEach((_, index) => {
				this.messageCounters[index].innerText = this.getMessageLengths()[index];
			});
		}
	}

	noCardOption() {
		this.noCard = true;
		this.cardId = "";
		this.cardSKU = "";
		this.categoryId = "";
		this.cardHeader = "";
		this.cardBody = "";
		this.cardFooter = "";
		this.previewUrl = "";

		this.dataAttributes = null;

		this.cardFormNoCard.classList.remove("hide");
		this.cardForm.classList.add("hide");
		this.closeCardsBox();
		this.markSelectedCard();
	}

	enterMessage() {
		this.messageCounters.forEach((_, index) => {
			this.messageCounters[index].innerText = this.getMessageLengths()[index];
		});
		this.checkMessage(false);
		this.infoMessages(this.errorMessages.blankCard, true);
	}

	checkMessage(cleaning) {
		if (!this.allowEmojis) this.hasEmoji();
		if (!this.allowEmojis && cleaning) this.cleanEmojis();
	}

	selectCard({ target }) {
		const card = target.parentNode;
		const cardId = card.getAttribute("data-id");
		const cardSKU = card.getAttribute("data-sku");
		if (!cardId) return;
		const selectedImg = card.querySelector("img");
		this.cardId = cardId;
		this.cardSKU = cardSKU;
		this.previewUrl = selectedImg.dataset.previewSrc;
		this.previewCard.src = this.previewUrl;
		this.noCard = false;
		this.markSelectedCard();

		this.openMessageBox();
	}

	selectCategory({ target }) {
		if (target.tagName.toLowerCase() !== "li") return;
		const selectedCategoryId = target.getAttribute("data-category");
		if (!selectedCategoryId) return;
		if (selectedCategoryId === "0") {
			// noCardOption();
			return;
		}
		this.categoryId = selectedCategoryId;
		this.categoryTitle.innerText = target.textContent;

		// MOBILE set category as selected and display cards
		this.categoriesBox.classList.add("selected");
		this.closeCardSelBtn.classList.add("hidden-sm");
		this.backBtn.classList.remove("hidden-sm");

		// Mark selected category btn
		Array.from(this.categoryBtns).forEach((categoryBtn) => {
			if (target.isEqualNode(categoryBtn)) {
				categoryBtn.classList.add("selected");
			} else {
				categoryBtn.classList.remove("selected");
			}
		});

		// Show selected group
		Array.from(this.cardGroups).forEach((group) => {
			if (this.categoryId === group.getAttribute("data-category")) {
				group.classList.add("show");
			} else {
				group.classList.remove("show");
			}
		});
	}

	openCardsBox() {
		if (this.noCard) {
			this.cardFormNoCard.classList.add("hide");
			this.cardForm.classList.remove("hide");
		}
		if (this.firstOpen === "MESSAGE") {
			this.cardMessageBox.classList.remove("show");
			this.cardSelectBox.classList.add("show");
			return;
		}
		this.overlay.classList.add("show");
		this.bodyEl.classList.add("stop-scrolling");
		this.cardSelectBox.classList.add("show");
		this.cardsContainer.setAttribute("data-active", true);
	}

	openMessageBox() {
		this.cardSelectBox.classList.remove("show");
		this.overlay.classList.add("show");
		this.bodyEl.classList.add("stop-scrolling");
		this.cardMessageBox.classList.add("show");
		this.cardMessageEditors[0].focus();
	}

	openSelector() {
		this.metaViewport.setAttribute("content", "width=device-width, initial-scale=1,maximum-scale=1");
		if (this.firstOpen === "CARDS") {
			this.openCardsBox();
		} else {
			this.openMessageBox();
		}
	}

	closeCardsBox() {
		if (this.firstOpen === "CARDS" || this.noCard) {
			this.cardSelectBox.classList.remove("show");
			this.openMessageBox();
			return;
		}
		this.cardSelectBox.classList.remove("show");
		this.overlay.classList.remove("show");
		this.bodyEl.classList.remove("stop-scrolling");
	}

	closeMessageBox() {
		if (this.firstOpen === "MESSAGE") {
			this.cardMessageBox.classList.remove("show");
			this.openCardsBox();
			return;
		}
		this.overlay.classList.remove("show");
		this.bodyEl.classList.remove("stop-scrolling");
		this.cardMessageBox.classList.remove("show");
	}

	closeSelector(e, done = false) {
		this.cardMessageBox.classList.remove("show");
		this.cardSelectBox.classList.remove("show");
		this.overlay.classList.remove("show");
		this.bodyEl.classList.remove("stop-scrolling");
		this.cardsContainer.setAttribute("data-active", false);
		this.metaViewport.setAttribute("content", "width=device-width, initial-scale=1");
		// if finish don't call onclose
		if (!done) this.onClose(this);
	}

	backToCategoryList() {
		this.categoriesBox.classList.remove("selected");
		this.closeCardSelBtn.classList.remove("hidden-sm");
		this.backBtn.classList.add("hidden-sm");
		this.categoryTitle.innerHTML = this.defaultTitle;
	}

	markSelectedCard() {
		if (this.cardId !== "" || this.cardSKU !== "") {
			const selectedCard =
				this.cardsContainer.querySelector(`.card[data-id='${this.cardId}']`) ||
				this.cardsContainer.querySelector(`.card[data-sku='${this.cardSKU}']`);

			if (!selectedCard) return;

			const selectedCategory = selectedCard.parentNode;
			this.cardId = selectedCard.getAttribute("data-id");
			this.cardSKU = selectedCard.getAttribute("data-sku");
			this.categoryId = selectedCategory.getAttribute("data-category");

			this.dataAttributes = selectedCard.dataset;

			selectedCategory.classList.add("show");

			// update preview image
			const selectedImg = selectedCard.querySelector("img");
			this.previewUrl = selectedImg.dataset.previewSrc;
			this.previewCard.src = this.previewUrl;
		}

		// Mark selected category btn
		Array.from(this.categoryBtns).forEach((categoryBtn) => {
			if (categoryBtn.getAttribute("data-category") === this.categoryId) {
				categoryBtn.classList.add("selected");
			} else {
				categoryBtn.classList.remove("selected");
			}
		});

		Array.from(this.cards).forEach((c) => {
			const cardId = c.getAttribute("data-id");
			if (cardId === this.cardId) {
				c.classList.add("selected");
			} else {
				c.classList.remove("selected");
			}
		});
	}

	// eslint-disable-next-line class-methods-use-this
	removeNewLines(editor, delta, current, source) {
		// Check if user is adding new line
		if (source !== "user" || delta.ops[delta.ops.length - 1].insert !== "\n") return;

		let insertIndex = delta.ops[0].retain;

		function flatten(arr) {
			const result = [];
			arr.forEach((item) => {
				if (Array.isArray(item)) {
					result.push(...item);
				} else {
					result.push(item);
				}
			});
			return result;
		}

		const newContent = flatten(
			current.ops.map((op) => {
				const splitContent = op.insert.split("");
				if (splitContent.length > insertIndex) {
					const prev = splitContent
						.slice(0, insertIndex)
						.filter(
							(item, index, arr) => item !== "\n" || index < 2 || item !== arr[index - 1] || item !== arr[index - 2]
						);

					const next = splitContent
						.slice(insertIndex)
						.filter(
							(item, index, arr) => item !== "\n" || index < 2 || item !== arr[index - 1] || item !== arr[index - 2]
						);

					if (next[0] === "\n") next.shift();

					const prevFinalNewLines = (prev[prev.length - 1] === "\n" ? 1 : 0) + (prev[prev.length - 2] === "\n" ? 1 : 0);

					insertIndex = Infinity;
					const insertContent = prev[prev.length - 1] === "\n" && next[0] === "\n" ? "" : "\n";

					return [
						{ insert: prev.join(""), attributes: op.attributes },
						prevFinalNewLines !== 2 ? { insert: insertContent } : undefined,
						{ insert: next.join(""), attributes: op.attributes },
					].filter(Boolean);
				}
				insertIndex -= splitContent.length;
				const newInsert = splitContent
					.filter(
						(item, index, arr) => item !== "\n" || index < 2 || item !== arr[index - 1] || item !== arr[index - 2]
					)
					.join("");

				return { insert: newInsert, attributes: op.attributes };
			})
		);
		editor.setContents([...newContent, { insert: "\n" }]);
	}

	addEvents() {
		this.overlay.addEventListener("click", this.closeSelector.bind(this));
		this.closeCardSelBtn.addEventListener("click", this.closeSelector.bind(this));
		this.closeCardMesBtn.addEventListener("click", this.closeSelector.bind(this));
		this.backBtn.addEventListener("click", this.backToCategoryList.bind(this));
		this.cardsHolder.addEventListener("click", this.selectCard.bind(this));
		this.categoriesBox.addEventListener("click", this.selectCategory.bind(this));
		this.openCardsBoxBtns.forEach((btn) => btn.addEventListener("click", this.openCardsBox.bind(this)));
		this.saveBtn.addEventListener("click", this.finish.bind(this));
		this.cardMessageEditors.forEach((editor, index) => {
			editor.on("editor-change", (eventName, ...args) => {
				if (eventName === "text-change") {
					const [delta, current, source] = args;
					this.removeNewLines(editor, delta, current, source);
				}
				if (editor.getLength() > this.maxlengthMsg[index]) {
					editor.deleteText(this.maxlengthMsg[index], editor.getLength());
				}
				this.enterMessage();
				const toolbarId = editor?.options?.modules?.toolbar?.container || null;
				if (!toolbarId) return;
				const toolbar = document.querySelector(toolbarId);
				if (!toolbar) return;
				const isActiveEditor = document?.activeElement?.closest(".ql-container")?.id === editor?.container?.id || false;
				toolbar.classList.toggle("active", isActiveEditor);
			});
		});

		this.leaveBlankCheckbox.addEventListener("change", this.handleBlankMessage.bind(this));
		this.noCardbtn.addEventListener("click", this.noCardOption.bind(this));
	}

	async createEditor() {
		try {
			const request = await asyncRequest({ url: this.cardsUrl, method: "GET" });
			if (!request || request.status !== 200 || !request.response) {
				throw new Error(`Can't fetch cards from url: ${this.cardsUrl}`);
			}

			const html = request.response;
			this.bodyEl = document.body;
			this.cardsContainer = document.createElement("div");
			this.cardsContainer.setAttribute("class", "cards-container");
			this.cardsContainer.setAttribute("data-active", false);
			this.overlay = document.createElement("div");
			this.overlay.setAttribute("id", "card-overlay");
			this.overlay.setAttribute("class", "card-overlay");

			const parser = new DOMParser();
			const htmlDom = parser.parseFromString(html, "text/html");
			this.cardSelectBox = htmlDom.getElementById("card-select");
			this.cardMessageBox = htmlDom.getElementById("card-message");

			this.cardsContainer.appendChild(this.overlay);
			this.cardsContainer.appendChild(this.cardSelectBox);
			this.cardsContainer.appendChild(this.cardMessageBox);

			this.closeCardSelBtn = this.cardsContainer.querySelector(".action > .close-card-select");
			this.closeCardMesBtn = this.cardsContainer.querySelector(".action > .close-card-message");
			this.backBtn = this.cardsContainer.querySelector(".action > .back");
			this.openCardsBoxBtns = this.cardsContainer.querySelectorAll(".open-cards-button");
			this.cardForm = this.cardsContainer.querySelector(".card-form");
			this.cardFormNoCard = this.cardsContainer.querySelector(".card-form.no-card");

			this.categoryTitle = this.cardSelectBox.querySelector(".box-title > .text");
			this.categoriesBox = this.cardsContainer.querySelector(".categories");
			this.categoryBtns = this.categoriesBox.getElementsByTagName("li");

			this.cardsHolder = this.cardsContainer.querySelector(".cards");
			this.cardGroups = this.cardsContainer.querySelectorAll(".card-group");
			this.cardMessageTitle = this.cardMessageBox.querySelector(".box-title > .text");
			this.cards = this.cardsContainer.querySelectorAll(".card");
			this.noCardbtn = this.cardsContainer.querySelector(".no-card-btn");

			this.cardHeaderEle = this.cardsContainer.querySelector("#cardHeader");
			this.cardBodyEle = this.cardsContainer.querySelector("#cardBody");
			this.cardFooterEle = this.cardsContainer.querySelector("#cardFooter");
			this.messageCounters = this.cardsContainer.querySelectorAll(".message-counter");
			this.leaveBlankCheckbox = this.cardsContainer.querySelector("#leave-blank");
			this.messageInfo = this.cardsContainer.querySelector(".message-info");

			this.previewCard = this.cardsContainer.querySelector("#preview-card");

			this.saveBtn = this.cardsContainer.querySelector("#card-save");

			this.bodyEl.appendChild(this.cardsContainer);

			this.metaViewport = document.querySelector("meta[name=viewport]");
			// Fill initial values
			// if cardId not informed, use the default from shopify card collection
			if (this.cardId === "") {
				const defaultId = this.cardSelectBox.getAttribute("data-default-card-id");
				if (defaultId) this.cardId = defaultId;
			}
			this.cardMessageTitle.innerHTML = this.defaultTitle;
			this.categoryTitle.innerHTML = this.defaultTitle;
			this.saveBtn.innerText = this.saveLabel;

			this.cardHeaderEle.innerHTML = this.cardHeader;
			this.cardBodyEle.innerHTML = this.cardBody;
			this.cardFooterEle.innerHTML = this.cardFooter;

			const Font = Quill.import("formats/font");
			Font.whitelist = ["LibreBaskerville", "BrownLLPanTT", "Moderat"];
			Quill.register(Font, true);
			Quill.register("modules/clipboard", PlainClipboard, true);

			this.cardHeaderEditor = new Quill("#cardHeader", {
				modules: {
					toolbar: "#cardHeaderToolbar",
				},
				placeholder: "To",
				theme: "snow",
			});
			this.cardBodyEditor = new Quill("#cardBody", {
				modules: {
					toolbar: "#cardBodyToolbar",
				},
				placeholder: "Message",
				theme: "snow",
			});
			this.cardFooterEditor = new Quill("#cardFooter", {
				modules: {
					toolbar: "#cardFooterToolbar",
				},
				placeholder: "From",
				theme: "snow",
			});
			this.cardMessageEditors = [this.cardHeaderEditor, this.cardBodyEditor, this.cardFooterEditor];
			this.leaveBlankCheckbox.checked = this.leaveBlank;

			this.addEvents();
			this.handleBlankMessage();
			this.markSelectedCard();
			this.readyCallback(this);
		} catch (error) {
			this.onError(error, this);
			throw error;
		}
	}
}
